import React, { FunctionComponent, useEffect, useState } from 'react'
import { Button, Heading, Modal, Radio, Text, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import {
  resetApplePayError,
  resetCheckoutError,
  selectPurchaseMethod,
  updatePaymentOption,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  usePbiPaymentOption,
  usePclPaymentOptions,
  useSelectedPaymentOption,
} from '../../state'
import { PaymentOptionLabel } from '../PaymentOptionLabel'
import { useCheckoutSizes } from '../../utils/responsive'
import { PaymentOption } from '../../model'
import { useTranslation } from '../../utils/convergence'
import { Route } from '../../config/router'
import styles from './style.module.css'
import { PaymentType } from '@afterpay/types'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { InstanceProps } from 'react-modal-promise'

interface Props {
  isOpen: boolean
  onResolve?: (paymentType: PaymentType) => void
  onReject?: () => void
}

export const PaymentOptionsModal: FunctionComponent<Props & InstanceProps<unknown>> = ({ isOpen, onResolve, onReject }) => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { textSize, headingSize } = useCheckoutSizes()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const router = useAppRouter()
  const initialPaymentOption = useSelectedPaymentOption()
  const pclPaymentOptions = usePclPaymentOptions()
  const pbiPaymentOption = usePbiPaymentOption()
  const paymentOptions = typeof pbiPaymentOption === 'undefined' ? pclPaymentOptions : [pbiPaymentOption, ...pclPaymentOptions]
  const [lock, setLock] = useState<boolean>(false)
  const [selectedId, setSelectedId] = useState<number | undefined>(initialPaymentOption?.id)
  const selectedPaymentOption = paymentOptions.find((paymentOption) => paymentOption.id === selectedId)
  const [paymentOptionsUpdated, setPaymentOptionsUpdated] = useState(false)
  const paymentMethod = useAppropriatePaymentMethod()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const handleContinueClick = async (): Promise<void> => {
    setLock(true)

    logEvent(TrackingEvent.PRESSED_CL_EDIT_PLAN_PAGE_CONTINUE_BUTTON, { paymentOption: selectedPaymentOption })

    if (selectedId === initialPaymentOption?.id) {
      onResolve(selectedPaymentOption?.paymentType)
      return
    }

    try {
      await dispatch(updatePaymentOption(selectedPaymentOption?.id)).unwrap()
      setPaymentOptionsUpdated(true)
      resetCheckoutError()
      resetApplePayError()
    } catch (e) {
      router.push(Route.TERMINAL_ERROR)
      onReject()
    }
  }

  useEffect(() => {
    if (paymentOptionsUpdated) {
      if (paymentMethod !== null) {
        // not expected to have no payment method at this stage in the flow
        dispatch(selectPurchaseMethod(paymentMethod, false))
      }
      router.replace(Route.SUMMARY)
      onResolve(selectedPaymentOption?.paymentType)
    }
  }, [paymentOptionsUpdated])

  const renderPaymentOption = (paymentOption: PaymentOption): JSX.Element => {
    const selected = paymentOption.id === selectedId

    return (
      <Radio.Button
        key={`payment-option${paymentOption.id}`}
        value={paymentOption.id.toString()}
        roundedBorder
        selectedBorder={selected && !isArcadeDesignLanguage}
        onChange={() => setSelectedId(paymentOption.id)}
        label={() => <PaymentOptionLabel paymentOption={paymentOption} selected={selected} />}
      />
    )
  }

  return (
    <Modal show={isOpen} onClose={onReject} showCloseButton>
      <div className={styles.container}>
        <Modal.Header>
          <Heading size={headingSize}>{t('consumerLending:paymentOptionsModal.title')}</Heading>
          <Text size={textSize}>{t('consumerLending:paymentOptionsModal.message')}</Text>
        </Modal.Header>
        <Modal.Content>
          <div className={styles.paymentOptionsContainer}>
            <Radio.Group
              id='payment-options-modal'
              name='payment-options-modal'
              testNameSpace='payment-options-modal'
              defaultValue={selectedId?.toString()}
              reversed
              kind='Option'
              labelWidth='Full'
              labelPlacement='Top'
              disabled={lock}
            >
              {paymentOptions.map(renderPaymentOption)}
            </Radio.Group>
          </div>
          <Button
            kind='Primary'
            padding='Fluid'
            size='Large'
            testNameSpace='payment-options-modal'
            onClick={handleContinueClick}
            disabled={lock}
            loading={lock}
          >
            {t('common:continue')}
          </Button>
        </Modal.Content>
        <TrackOnMount eventName={TrackingEvent.VIEWED_CL_EDIT_PLAN_PAGE} />
      </div>
    </Modal>
  )
}
